import { Injectable } from '@angular/core';
import { environment } from '@environment/environment';
import { Subject } from 'rxjs';
import { debounceTime } from 'rxjs/operators';

// The two public forms the configuration panel can preview, each rendered by
// the references app against an unsaved sample built from the firm's current
// settings.
export type FormPreviewKind = 'reference-request' | 'reference';

export interface IFormPreviewParams {
  lng?: string;
  /** Reference form only: the generated sample set or the firm default. */
  question_set?: 'sample' | 'default';
}

// Opens a preview in its own tab and nudges that tab to re-fetch whenever a
// setting is saved here (see PreviewRefreshInterceptor). The two apps are
// different origins, so the nudge is a postMessage to the window handle with
// an explicit target origin.
@Injectable({
  providedIn: 'root'
})
export class FormPreviewService {
  public static readonly PATHS: Record<FormPreviewKind, string> = {
    'reference-request': '/reference-requests/preview',
    reference: '/references/preview'
  };
  public static readonly WINDOW_NAMES: Record<FormPreviewKind, string> = {
    'reference-request': 'vitay-reference-request-preview',
    reference: 'vitay-reference-preview'
  };
  public static readonly REFRESH_MESSAGES: Record<FormPreviewKind, string> = {
    'reference-request': 'vitay:reference-request-preview:refresh',
    reference: 'vitay:reference-preview:refresh'
  };
  public static readonly REFRESH_DEBOUNCE_MS = 400;

  private readonly previewBase = String(environment.ENDPOINTS.FRONT_END.REFERENCES);
  private readonly previewOrigin = String(new URL(this.previewBase).origin);

  private readonly windows: Partial<Record<FormPreviewKind, Window | null>> = {};
  private readonly changed$ = new Subject<void>();

  constructor() {
    // Several autosaves can land within a second (a toggle plus its
    // sub-toggle, a debounced editor); one refresh is enough.
    this.changed$
      .pipe(debounceTime(FormPreviewService.REFRESH_DEBOUNCE_MS))
      .subscribe(() => this.postRefresh());
  }

  public isOpen(kind: FormPreviewKind): boolean {
    const handle = this.windows[kind];
    return !!handle && !handle.closed;
  }

  public get isAnyOpen(): boolean {
    return (Object.keys(this.windows) as FormPreviewKind[]).some((kind) =>
      this.isOpen(kind)
    );
  }

  public previewUrl(kind: FormPreviewKind, params: IFormPreviewParams = {}): string {
    const url = new URL(FormPreviewService.PATHS[kind], this.previewBase);
    if (params.lng) {
      url.searchParams.set('lng', params.lng);
    }
    if (params.question_set) {
      url.searchParams.set('question_set', params.question_set);
    }
    return url.toString();
  }

  // A named target reuses the same tab on every click (and re-acquires the
  // handle after this app was reloaded), reloading it fresh.
  public open(kind: FormPreviewKind, params: IFormPreviewParams = {}): Window | null {
    this.windows[kind] = window.open(
      this.previewUrl(kind, params),
      FormPreviewService.WINDOW_NAMES[kind]
    );
    return this.windows[kind];
  }

  public notifyChanged(): void {
    if (this.isAnyOpen) {
      this.changed$.next();
    }
  }

  private postRefresh(): void {
    (Object.keys(this.windows) as FormPreviewKind[]).forEach((kind) => {
      if (this.isOpen(kind)) {
        this.windows[kind].postMessage(
          { type: FormPreviewService.REFRESH_MESSAGES[kind] },
          this.previewOrigin
        );
      }
    });
  }
}
